import "dayjs/locale/id";
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import calendar from "dayjs/plugin/calendar";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(calendar);

const INDO_ZONE_NAME: Record<number, string> = {
  7: "WIB",
  8: "WITA",
  9: "WIT",
};

export const getIndoZoneName = (date: any) => {
  const timezoneOffset = Math.abs(dayjs(date).utcOffset() / 60);
  return INDO_ZONE_NAME?.[timezoneOffset] || "";
};

type FormatDateParams = {
  date?: Date | string | Dayjs | null;
  format?: string;
  fallbackValue?: string;
  timezone?: string | null;
  locale?: string;
};

export const formatDate = (
  dateOrParams: Date | string | Dayjs | null | FormatDateParams,
  format = "DD MMM YYYY, HH:mm",
  fallbackValue = "-",
  timezone: string | null = null,
  locale: string = "id"
) => {
  let date: Date | string | Dayjs | null | FormatDateParams = "";

  if (
    typeof dateOrParams === "object" &&
    !(dateOrParams instanceof Date) &&
    !dayjs.isDayjs(dateOrParams)
  ) {
    ({
      date = "",
      format = "DD MMM YYYY, HH:mm",
      fallbackValue = "-",
      timezone = null,
      locale = "id",
    } = dateOrParams || {});
  } else {
    date = dateOrParams || "";
  }

  dayjs.locale(locale);
  const dateDayjs = !!timezone ? dayjs(date).tz(timezone) : dayjs(date);

  if (!dateDayjs.isValid()) return fallbackValue;

  if (typeof format === "object") {
    return dateDayjs.calendar(null, format);
  }

  return dateDayjs.format(format);
};

/*
  Input: 2002-01-01
  Output: January 2002
*/
export const formatOnlyMonthYear = (date: any, language = "id", monthFormat = "MMMM") => {
  dayjs.locale(language);
  return dayjs(date).format(`${monthFormat} YYYY`);
};

export const expiredTextTemplate = {
  nullText: "Tidak ada batas berlaku",
  expired: "Mohon maaf tiket ini sudah expired",
  minutes: ["Berakhir dalam", "menit lagi!"],
  hours: ["Berakhir dalam", "jam lagi!"],
  days: ["Berakhir", "hari lagi!"],
  rest: ["Berlaku hingga", ""],
};

export const expireTextDefaultDeadline = {
  minutes: 60,
  hours: 24,
  days: 7,
  months: null,
};

export const getExpiredText = (expiredDate: any, template = {}, deadline: any) => {
  const now = dayjs();
  const expiredTime = dayjs(expiredDate);
  const { nullText, expired, minutes, days, hours, rest } = { ...expiredTextTemplate, ...template };
  const {
    minutes: minutesDeadline,
    hours: hoursDeadline,
    days: daysDeadline,
    months: monthsDeadline,
  }: any = { ...expireTextDefaultDeadline, ...deadline };

  const diffTime = expiredTime.diff(now);
  const diffDuration = dayjs.duration(diffTime);
  const diffMinutes = Math.ceil(diffDuration.asMinutes());
  const diffHours = Math.ceil(diffDuration.asHours());
  const diffDays = Math.ceil(diffDuration.asDays());

  if (expiredDate === null) {
    return nullText;
  }

  if (diffTime < 0) {
    return expired;
  }

  if (diffMinutes < minutesDeadline && minutesDeadline) {
    return `${minutes[0]} ${diffMinutes} ${minutes[1]}`;
  }

  if (diffHours <= hoursDeadline && hoursDeadline) {
    return `${hours[0]} ${diffHours} ${hours[1]}`;
  }

  if (diffDays <= daysDeadline && daysDeadline) {
    return `${days[0]} ${diffDays} ${days[1]}`;
  }

  return `${rest[0]} ${expiredTime.format("DD MMM YYYY")} ${rest[1]}`;
};

export const addDate = (date: string, value: number, unit: any) => {
  return dayjs(date).add(value, unit).toDate();
};
